import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ShieldOff, ShieldQuestion, Plus, Minus } from "lucide-react";
import type Contact from "@/model/interface/Contact";
import GrantConsentDialog from "@/components/pages/contacts/GrantConsentDialog";
import RevokeConsentDialog from "@/components/pages/contacts/RevokeConsentDialog";

const CONSENT_CONFIG = {
  EXPRESS: { label: "Express Consent", Icon: ShieldCheck, color: "text-green-600" },
  WRITTEN: { label: "Written Consent", Icon: ShieldCheck, color: "text-green-600" },
  NONE: { label: "No Consent", Icon: ShieldOff, color: "text-destructive" },
  UNKNOWN: { label: "Unknown", Icon: ShieldQuestion, color: "text-muted-foreground" },
};

const CONSENT_TYPE_LABELS: Record<string, string> = {
  CALL: "Calling",
  SMS: "SMS",
  CALL_AND_SMS: "Calling & SMS",
  NONE: "None",
};

const BASIS_LABELS: Record<string, string> = {
  EXPRESS: "Express",
  WRITTEN: "Written",
};

interface ContactConsentStatusProps {
  contact: Contact;
}

const ContactConsentStatus: React.FC<ContactConsentStatusProps> = ({ contact }) => {
  const [showGrantDialog, setShowGrantDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);

  const status = contact.consentStatus ?? "UNKNOWN";
  const config = CONSENT_CONFIG[status];

  return (
    <div className="px-4 py-3 border-b border-border">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <config.Icon className={`h-4 w-4 ${config.color}`} />
          <span className="text-sm font-medium">Consent</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setShowGrantDialog(true)}>
            <Plus className="h-3 w-3 mr-1" />
            Grant
          </Button>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setShowRevokeDialog(true)}>
            <Minus className="h-3 w-3 mr-1" />
            Revoke
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-2 mb-1.5">
        <Badge variant={status === "NONE" ? "destructive" : status === "UNKNOWN" ? "secondary" : "default"} className="text-xs">
          {config.label}
        </Badge>
      </div>
      {contact.phones && contact.phones.length > 0 && (
        <div className="space-y-1">
          {contact.phones.map((phone, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="font-mono text-muted-foreground">{phone.phoneNumber || phone.phoneNumberE164}</span>
              <span className="text-muted-foreground">
                {CONSENT_TYPE_LABELS[phone.consentType ?? "NONE"]}
                {phone.consentType && phone.consentType !== "NONE" && phone.consentStatus && BASIS_LABELS[phone.consentStatus]
                  ? ` · ${BASIS_LABELS[phone.consentStatus]}`
                  : ""}
              </span>
            </div>
          ))}
        </div>
      )}

      <GrantConsentDialog
        contactId={contact.id}
        phones={contact.phones}
        open={showGrantDialog}
        onOpenChange={setShowGrantDialog}
      />
      <RevokeConsentDialog
        contactId={contact.id}
        phones={contact.phones}
        consentStatus={contact.consentStatus}
        open={showRevokeDialog}
        onOpenChange={setShowRevokeDialog}
      />
    </div>
  );
};

export default ContactConsentStatus;
